import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Camera, ArrowRight, Shield
} from 'lucide-react';
import {
  PhoneMockup, DashboardMockupContent
} from '@/components/PhoneMockup';
import axios from 'axios';

import { C, HERO_IMG } from '@/components/landing/LandingConstants';
import { AuthPanel } from '@/components/landing/AuthPanel';
import {
  StatsBar, HowItWorks, AccuracySection, CoachSection,
  OptimizeSection, FeaturesGrid, PrivacySection, GLP1Section,
  HealthModesSection, GentleModeSection, RegionalCuisineSection,
  MFPSection, DatabaseSection, PricingSection, BetaCTA,
  FinalCTA, NatureFooter
} from '@/components/landing/LandingSections';

const API = '/api';

export default function Landing() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes('@') || !email.split('@')[1]?.includes('.')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!form.password || form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!isLogin && (!form.name || !form.name.trim())) {
      setError('Name is required.');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password: form.password } : { ...form, email };
      const res = await axios.post(`${API}${endpoint}`, payload);
      if (!res.data || !res.data.user || !res.data.token) {
        setError('Unexpected response from server. Please try again.');
        setLoading(false);
        return;
      }
      login(res.data.user, res.data.token);
      if (res.data.user.onboarding_complete) navigate('/dashboard');
      else navigate('/onboarding');
    } catch (err) {
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(detail)) {
          setError(detail.map(d => d.msg || d.message || JSON.stringify(d)).join('. '));
        } else {
          setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
      } else if (err.code === 'ERR_NETWORK') {
        setError('Unable to connect. Please check your internet connection.');
      } else {
        setError('Login failed. Please verify your email and password.');
      }
    }
    setLoading(false);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: C.bg, color: C.text, fontFamily: 'var(--font-sans)' }} data-testid="landing-page">

      {/* Header */}
      <header className="sticky top-0 z-50 px-6 py-4 md:px-12" style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/nuri-logo-mark.svg" alt="nuri" style={{ height: 36 }} />
          </div>
          <nav className="hidden md:flex items-center gap-6">
            {['how-it-works', 'full-picture', 'features', 'pricing'].map(id => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-sm transition-colors hover:opacity-70" style={{ color: C.textSec, background: 'none', border: 'none', cursor: 'pointer' }}>
                {id === 'how-it-works' ? 'How It Works' : id === 'full-picture' ? 'The Full Picture' : id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            ))}
            <Button data-testid="nav-sign-up" onClick={() => { setShowAuth(true); setIsLogin(false); }}
              className="text-sm h-9 px-4 rounded-lg font-medium" style={{ background: C.terracotta, color: '#fff', border: 'none' }}>
              Sign Up
            </Button>
            <Button data-testid="nav-sign-in" onClick={() => { setShowAuth(true); setIsLogin(true); }}
              className="text-sm h-9 px-4 rounded-lg" style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.text }}>
              Sign In
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="hero" className="relative">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${C.bg} 0%, ${C.bg}dd 40%, ${C.bg}88 70%, ${C.bg}44 100%)` }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${C.bg} 0%, transparent 50%)` }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="max-w-2xl lg:flex-1">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, color: C.text }}>
                Fuel your body & mind.<br />
                See what you're <span style={{ color: C.green }}>really</span> eating.
              </h1>
              <p className="text-base lg:text-lg leading-relaxed mb-10 max-w-lg" style={{ color: C.textSec }}>
                1,050+ recipes across 12 health modes, AI photo analysis with smart follow-ups, and Nuri AI Coach that knows your complete data. From weight loss to GLP-1 support to gentle ED recovery.
              </p>
              {!showAuth && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button data-testid="get-started-btn" onClick={() => setShowAuth(true)}
                    className="h-13 px-8 text-base font-semibold rounded-xl text-white transition-all nuri-btn-press"
                    style={{ background: C.accent, border: 'none' }}>
                    Start Tracking Free <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button data-testid="sign-in-link" onClick={() => { setShowAuth(true); setIsLogin(true); }}
                    className="h-13 px-8 text-base font-medium rounded-xl"
                    style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}>
                    Sign In
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-4 mt-8 text-xs" style={{ color: C.muted }}>
                <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> No credit card required</span>
                <span className="hidden sm:block">|</span>
                <span className="hidden sm:flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Nuri AI Vision</span>
              </div>
            </div>
            <div className="hidden lg:block lg:flex-shrink-0" data-testid="hero-phone-mockup">
              <PhoneMockup angle><DashboardMockupContent /></PhoneMockup>
            </div>
            <div className="lg:hidden flex justify-center" data-testid="hero-phone-mockup-mobile">
              <PhoneMockup><DashboardMockupContent /></PhoneMockup>
            </div>
          </div>
        </div>
      </section>

      <StatsBar />
      <HowItWorks />
      <AccuracySection />
      <CoachSection setShowAuth={setShowAuth} />
      <OptimizeSection />
      <FeaturesGrid />
      <PrivacySection />
      <GLP1Section />
      <HealthModesSection />
      <GentleModeSection />
      <RegionalCuisineSection />
      <MFPSection />
      <DatabaseSection />
      <BetaCTA setShowAuth={setShowAuth} />
      <PricingSection setShowAuth={setShowAuth} />
      <FinalCTA setShowAuth={setShowAuth} />
      <NatureFooter />

      {/* Auth Modal */}
      {showAuth && (
        <div data-testid="auth-modal-overlay" className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuth(false); }}>
          <div className="absolute inset-0" style={{ background: 'rgba(26,26,24,0.4)', backdropFilter: 'blur(4px)' }} />
          <div className="relative w-full max-w-md my-4 sm:my-0 animate-fade-in-up">
            <AuthPanel isLogin={isLogin} setIsLogin={setIsLogin} form={form} setForm={setForm}
              handleSubmit={handleSubmit} handleGoogleAuth={handleGoogleAuth}
              error={error} loading={loading} showPassword={showPassword} setShowPassword={setShowPassword}
              onClose={() => setShowAuth(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
